import { Injectable, computed, effect, inject, signal } from '@angular/core';
import { Observable, Subscription } from 'rxjs';
import { tap } from 'rxjs/operators';
import {
  Comment,
  CommentsRepositoryService,
  Events,
  WebSocketService
} from '@xmedical/core';
import { PatientStore } from './patient.store';
/**
 * PatientCommentsStore — clinical comments for the current patient.
 *
 * Holds every comment the user is authorised for (single fetch, no
 * commentTypes query param — server returns all). Multiple comments
 * widgets on the same page filter the flat list via `computed`:
 *
 *   readonly comments = computed(() =>
 *     commentsStore.items().filter(c => this.types.includes(c.commentType.id))
 *   );
 *
 * Push event reloads once; every widget re-renders via the signal.
 *
 * NOTE: The xmedical OpenAPI doc declares `commentTypes` as `required: true`
 * for `GET /v1/Comments/patient/{id}`, but the backend actually treats
 * an absent / empty value as "return all". A separate backend follow-up
 * will align the contract — once that lands the empty-array branch in
 * CommentsRepositoryService.loadPatientComments becomes redundant but
 * harmless.
 */
@Injectable({
  providedIn: 'root'
})
export class PatientCommentsStore {
  private patientStore = inject(PatientStore);
  private repo = inject(CommentsRepositoryService);
  private websocket = inject(WebSocketService);

  private readonly _items = signal<Comment[]>([]);
  private readonly _loading = signal<boolean>(false);
  private readonly _error = signal<string | null>(null);

  readonly items = this._items.asReadonly();
  readonly loading = this._loading.asReadonly();
  readonly error = this._error.asReadonly();
  readonly count = computed(() => this._items().length);

  private currentChannel: string | null = null;
  private readonly eventName = Events.CommentCommentsChanged;
  // Cancels any in-flight `fetch()` HTTP when a newer request arrives.
  private fetchSub?: Subscription;

  constructor() {
    effect(() => {
      const patient = this.patientStore.current();
      const channel = this.patientStore.patientEventsChannelName();

      if (this.currentChannel && this.currentChannel !== channel) {
        this.websocket.unsubscribe(this.currentChannel, this.eventName);
        this.currentChannel = null;
      }

      if (!patient) {
        this.fetchSub?.unsubscribe();
        this._loading.set(false);
        this._items.set([]);
        this._error.set(null);
        return;
      }

      this.fetch(patient.id);

      if (channel && this.currentChannel !== channel) {
        // Push payload carries `{type: number}` for the changed type — we
        // reload everything regardless because the store holds all types.
        this.websocket.subscribe(channel, this.eventName, () => this.reload());
        this.currentChannel = channel;
      }
    });
  }

  reload(): void {
    const patient = this.patientStore.current();
    if (!patient) return;
    this.fetch(patient.id);
  }

  /**
   * Fetch a single comment by id (one-off lookup for the draft editor;
   * does not affect the list signal).
   */
  load(commentId: number): Observable<Comment> {
    return this.repo.get(commentId);
  }

  /**
   * Create a new comment. Returns the new id; caller registers via
   * DraftsService for editing. The list reloads on success so the new
   * draft appears in widgets that show this comment's type.
   */
  create(comment: Comment): Observable<number> {
    return this.repo.create(comment).pipe(
      tap({
        next: () => this.reload()
      })
    );
  }

  /**
   * Update a comment (used by the draft editor for incremental autosave
   * and the explicit lock on commit). Reloads the list when locked so
   * the saved entry appears.
   */
  update(comment: Comment): Observable<boolean> {
    return this.repo.update(comment).pipe(
      tap({
        next: success => {
          if (success && comment.locked) this.reload();
        }
      })
    );
  }

  private fetch(patientId: number): void {
    this.fetchSub?.unsubscribe();
    this._loading.set(true);
    this._error.set(null);
    this.fetchSub = this.repo.loadPatientComments(patientId).subscribe({
      next: comments => {
        const sorted = [...comments].sort(
          (a, b) => new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime()
        );
        this._items.set(sorted);
        this._loading.set(false);
      },
      error: error => {
        this._loading.set(false);
        this._error.set('Could not load comments for this patient');
        
      }
    });
  }
}
